<template>
  <messenger-widget-welcome-view
    :welcome-view-settings="welcomeViewSettings"
    :pre-chat-view-settings="preChatViewSettings"
    :pre-chat-form-step="preChatFormStep"
    :is-pre-chat-submitting="isPreChatSubmitting"
    :chat-state="chatState"
    :number-of-new-messages="numberOfNewMessages"
    :last-message="lastMessage"
    @click-channel-button="onClickChannel"
    @pre-chat-change-form-step="changePreChatFormStep"
    @pre-chat-submit="submit"
    @back-to-chat="onClickChannel(WebWidgetChannelType.CHAT)"
    @resize="$emit('resize', $event)"
  >
    <template v-slot:alert>
      <messenger-alert-container
        :scopes="['preChat', 'chatConnection']"
        is-rounded
      />
    </template>
  </messenger-widget-welcome-view>
</template>

<script lang="ts" setup>
import MessengerWidgetWelcomeView from '@widgets/messenger/components/views/MessengerWidgetWelcomeView.vue'
import { useWidgetWelcomeViewSettings } from '@widgets/messenger/compositions/useWidgetWelcomeViewSettings'
import { useWidgetNavigation } from '@widgets/messenger/compositions/useWidgetNavigation'
import { useWidgetFormCache } from '@widgets/messenger/compositions/useWidgetFormCache'
import { useWidgetPreChatViewSettings } from '@widgets/messenger/compositions/useWidgetPreChatViewSettings'
import { useWidgetChatInit } from '@widgets/messenger/compositions/useWidgetChatInit'
import { useFormFieldMap } from '@widgets/messenger/compositions/useFormFieldMap'
import translateService from '@widgets/shared/services/translate/translateService'
import { isValidEmail } from '@widgets/shared/utils/validators/isValidEmail'
import type { MessengerPreChatForm, PreChatFormStep } from '@widgets/messenger/types'
import { castCommonErrorToMessages } from '@widgets/messenger/utils/castCommonErrorToMessages'
import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import { useWidgetChatBroadcastEvents } from '@widgets/messenger/compositions/useWidgetChatBroadcastEvents'
import MessengerAlertContainer from '@widgets/messenger/containers/MessengerAlertContainer.vue'
import { useWidgetFormAlerts } from '@widgets/messenger/compositions/useWidgetFormAlerts'
import { useWidgetForm, type WidgetFieldsConfig } from '@/composition/widgetForm'
import { computed, onMounted, ref, watch } from '@/composition/vue/compositionApi'
import {
  WebWidgetChannelType,
  type WebWidgetDetailsPreChatFieldName,
} from '@/data/models/domain/messages/settings/webWidget/types'

defineEmits<{
  (e: 'resize', height: number): void
}>()

const welcomeViewSettings = useWidgetWelcomeViewSettings()
const preChatViewSettings = useWidgetPreChatViewSettings()
const { onClickChannel } = useWidgetNavigation()
const { startNewChat } = useWidgetChatInit()
const chatBroadcastEvents = useWidgetChatBroadcastEvents()
const { fieldMap: preChatFieldMap } = useFormFieldMap<WebWidgetDetailsPreChatFieldName>(
  () => preChatViewSettings.value.formFields,
)
const { chatState, numberOfNewMessages, lastMessage } = useChatStore()

const preChatFormStep = ref<PreChatFormStep>('message')
const changePreChatFormStep = (step: PreChatFormStep) => {
  preChatFormStep.value = step
}

const { formValues: formValuesCache, updateFormValues: updateFormValuesCache } = useWidgetFormCache()
const preChatFormValuesCache = computed(() => formValuesCache.value.preChat)
const updatePreChatFormValues = (value: Partial<MessengerPreChatForm>) => updateFormValuesCache('preChat', value)

const preChatFieldsConfig = {
  name: {
    value: preChatFormValuesCache.value.name ?? '',
    validate: preChatFieldMap.value.name.isRequired
      ? (value: string) => (!value ? [translateService.t('errors.name')] : [])
      : undefined,
  },
  email: {
    value: preChatFormValuesCache.value.email ?? '',
    validate: (value: string) => {
      const trimmedValue = value.trim()
      if (trimmedValue && isValidEmail(trimmedValue)) {
        return []
      }
      return [translateService.t('errors.email')]
    },
  },
  message: {
    value: preChatFormValuesCache.value.message ?? '',
    validate: (value: string) => (!value ? [translateService.t('errors.message')] : []),
  },
} satisfies WidgetFieldsConfig<MessengerPreChatForm>

const isPreChatSubmitting = ref(false)
const preChatSubmitCallback = async (preChatForm: MessengerPreChatForm) => {
  try {
    isPreChatSubmitting.value = true
    const payload: MessengerPreChatForm = {
      ...preChatForm,
      email: preChatForm.email.trim(),
    }
    await startNewChat(payload)
    setFieldValue('message', '')
    updatePreChatFormValues(form)
  } finally {
    isPreChatSubmitting.value = false
  }
  chatBroadcastEvents.emitChatStart()
  onClickChannel(WebWidgetChannelType.CHAT)
}

const { submit, form, commonErrors, setFieldValue } = useWidgetForm<MessengerPreChatForm>(
  preChatFieldsConfig,
  preChatSubmitCallback,
  {},
  castCommonErrorToMessages,
)
watch(
  form,
  (value) => {
    updatePreChatFormValues(value)
  },
  { deep: true },
)
const { initCommonErrorsListening } = useWidgetFormAlerts({
  scope: 'preChat',
  commonErrors,
})
onMounted(() => {
  initCommonErrorsListening()
})
</script>
